import { useEffect, useState, useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';

import { isInstance } from '@pega/cosmos-react-core';

import type { ImageAttributes } from '../ImageEditDialog';
import type { ImagePosition } from '../ImageActionButtons';

interface UseImageActionsParams {
  tiptapEditor: TiptapEditor | null;
  iframeRef: RefObject<HTMLIFrameElement>;
  isDialogOpen?: boolean;
}

interface UseImageActionsReturn {
  hoveredImagePosition: ImagePosition | null;
  currentImageAttrs: ImageAttributes | null;
  currentImageElement: HTMLImageElement | null;
  handleDelete: () => void;
  setIsOverButtons: (value: boolean) => void;
}

export default function useImageActions({
  tiptapEditor,
  iframeRef,
  isDialogOpen = false
}: UseImageActionsParams): UseImageActionsReturn {
  const [hoveredImagePosition, setHoveredImagePosition] = useState<ImagePosition | null>(null);
  const [currentImageAttrs, setCurrentImageAttrs] = useState<ImageAttributes | null>(null);
  const currentImgRef = useRef<HTMLImageElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOverImageRef = useRef(false);
  const isOverButtonsRef = useRef(false);
  const isDialogOpenRef = useRef(isDialogOpen);
  const wasDialogOpenRef = useRef(isDialogOpen);

  // Keep ref in sync with prop
  isDialogOpenRef.current = isDialogOpen;

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const hideButtons = useCallback(() => {
    setHoveredImagePosition(null);
    setCurrentImageAttrs(null);
    currentImgRef.current = null;
    isOverImageRef.current = false;
    isOverButtonsRef.current = false;
  }, []);

  const scheduleHide = useCallback(() => {
    // Don't hide while dialog is open
    if (isDialogOpenRef.current) return;

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      // Check ref for latest dialog state inside timeout
      if (!isOverImageRef.current && !isOverButtonsRef.current && !isDialogOpenRef.current) {
        hideButtons();
      }
    }, 100);
  }, [hideButtons]);

  // Expose a way to track if mouse is over the buttons (called from parent)
  const setIsOverButtons = useCallback(
    (value: boolean) => {
      isOverButtonsRef.current = value;
      if (!value) {
        scheduleHide();
      } else {
        cancelHide();
      }
    },
    [scheduleHide, cancelHide]
  );

  const positionButtons = useCallback(
    (img: HTMLImageElement) => {
      if (!iframeRef.current) return;

      const imgRect = img.getBoundingClientRect();
      const iframeRect = iframeRef.current.getBoundingClientRect();

      // Calculate position relative to viewport (accounting for iframe position)
      const position: ImagePosition = {
        top: iframeRect.top + imgRect.top,
        left: iframeRect.left + imgRect.left,
        width: imgRect.width,
        height: imgRect.height
      };

      setHoveredImagePosition(position);
      currentImgRef.current = img;

      // Get the link parent if the image is wrapped
      const linkParent = img.closest('a');

      setCurrentImageAttrs({
        src: img.src,
        alt: img.alt,
        width: img.width || undefined,
        height: img.height || undefined,
        actionUrl: linkParent?.getAttribute('href') || undefined
      });
    },
    [iframeRef]
  );

  useEffect(() => {
    const wasDialogOpen = wasDialogOpenRef.current;
    wasDialogOpenRef.current = isDialogOpen;

    // Refresh current attrs after edit dialog closes so reopen uses latest href state.
    if (wasDialogOpen && !isDialogOpen && currentImgRef.current) {
      positionButtons(currentImgRef.current);
    }
  }, [isDialogOpen, positionButtons]);

  const handleDelete = useCallback(() => {
    if (!currentImgRef.current || !tiptapEditor) return;

    // Get exact position from DOM element to handle duplicate images correctly
    const pos = tiptapEditor.view.posAtDOM(currentImgRef.current, 0);

    if (pos >= 0) {
      tiptapEditor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + 1 })
        .run();
    }

    hideButtons();
  }, [tiptapEditor, hideButtons]);

  // Set up image hover listeners inside the iframe
  useEffect(() => {
    if (!tiptapEditor || !iframeRef.current?.contentDocument) return;

    const iframeDoc = iframeRef.current.contentDocument;

    // Verify the editor is properly initialized in the iframe
    const proseMirrorEl = iframeDoc.querySelector('.ProseMirror');
    if (!proseMirrorEl) return;

    const imageListeners = new Map<HTMLImageElement, { enter: () => void; leave: () => void }>();

    const attachImageListeners = (img: HTMLImageElement) => {
      if (imageListeners.has(img)) return;

      const handleEnter = () => {
        cancelHide();
        isOverImageRef.current = true;
        positionButtons(img);
      };

      const handleLeave = () => {
        isOverImageRef.current = false;
        scheduleHide();
      };

      img.addEventListener('mouseenter', handleEnter);
      img.addEventListener('mouseleave', handleLeave);

      imageListeners.set(img, {
        enter: handleEnter,
        leave: handleLeave
      });
    };

    const detachImageListeners = (img: HTMLImageElement) => {
      const listeners = imageListeners.get(img);
      if (listeners) {
        img.removeEventListener('mouseenter', listeners.enter);
        img.removeEventListener('mouseleave', listeners.leave);
        imageListeners.delete(img);
      }
    };

    // Observe DOM changes to attach listeners to new images
    const setupImages = () => {
      const images = iframeDoc.querySelectorAll<HTMLImageElement>('.ProseMirror img');
      images.forEach(img => {
        attachImageListeners(img);
      });
    };

    // Initial setup
    setupImages();

    // Watch for new images and detect when current image is removed
    const observer = new MutationObserver(() => {
      setupImages();

      // Clean up listeners for images that are no longer in the document
      imageListeners.forEach((_listeners, img) => {
        if (!iframeDoc.body.contains(img)) {
          detachImageListeners(img);
        }
      });

      // If the current image was removed (e.g., via backspace), hide buttons
      if (currentImgRef.current && !iframeDoc.body.contains(currentImgRef.current)) {
        hideButtons();
      }
    });

    const proseMirror = iframeDoc.querySelector('.ProseMirror');
    if (proseMirror) {
      observer.observe(proseMirror, {
        childList: true,
        subtree: true
      });
    }

    // Hide buttons when clicking elsewhere in the iframe
    const handleDocClick = (e: Event) => {
      if (isInstance(e.target, HTMLElement) && e.target.tagName !== 'IMG') {
        hideButtons();
      }
    };

    iframeDoc.addEventListener('click', handleDocClick);

    // Reposition buttons on scroll inside iframe
    const handleScroll = () => {
      if (currentImgRef.current) {
        positionButtons(currentImgRef.current);
      }
    };

    const iframeBody = iframeDoc.body;
    iframeBody.addEventListener('scroll', handleScroll);

    // Reposition buttons on window resize
    const handleResize = () => {
      if (currentImgRef.current) {
        positionButtons(currentImgRef.current);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      observer.disconnect();

      imageListeners.forEach((_listeners, img) => {
        detachImageListeners(img);
      });

      iframeDoc.removeEventListener('click', handleDocClick);
      iframeBody.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [tiptapEditor, iframeRef, positionButtons, scheduleHide, cancelHide, hideButtons]);

  return {
    hoveredImagePosition,
    currentImageAttrs,
    currentImageElement: currentImgRef.current,
    handleDelete,
    setIsOverButtons
  };
}
